import type {
  DeleteFixedAppointment,
  DeleteFixedAppointmentMember,
  GetFixedAppointmentAppointments,
  GetFixedAppointmentChangeLog,
  GetFixedAppointmentMemberBookingPreview,
  GetFixedAppointments,
  PostFixedAppointmentMember,
  PutFixedAppointmentEndDate,
  PutFixedAppointmentMember,
} from "@/server-types";
import { queryClient } from "..";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { del, get, post, put } from "../request-helpers";

function invalidateFixedAppointmentQueries() {
  queryClient.invalidateQueries({ queryKey: ["fixed-appointments"] });
  queryClient.invalidateQueries({
    queryKey: ["fixed-appointment-appointments"],
  });
  queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
}

/**
 * GET /fixed-appointment
 */
export const useGetFixedAppointments = (
  query: GetFixedAppointments["query"] & {
    enabled?: boolean;
  },
) => {
  const { enabled, ...params } = query;
  return useCustomQuery({
    queryKey: ["fixed-appointments", params],
    queryFn: () => get<GetFixedAppointments>("/fixed-appointment", params),
    enabled: (enabled ?? true) && params.locationId > 0,
  });
};

/**
 * GET /fixed-appointment/:fixedAppointmentId/appointments
 */
export const useGetFixedAppointmentAppointments = ({
  fixedAppointmentId,
  enabled,
}: {
  fixedAppointmentId: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["fixed-appointment-appointments", fixedAppointmentId],
    queryFn: () =>
      get<GetFixedAppointmentAppointments>(
        `/fixed-appointment/${fixedAppointmentId}/appointments`,
      ),
    enabled: (enabled ?? true) && fixedAppointmentId > 0,
  });
};

/**
 * GET /fixed-appointment/:fixedAppointmentId/change-log
 */
export const useGetFixedAppointmentChangeLog = ({
  fixedAppointmentId,
  enabled,
}: {
  fixedAppointmentId: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["fixed-appointment-change-log", fixedAppointmentId],
    queryFn: () =>
      get<GetFixedAppointmentChangeLog>(
        `/fixed-appointment/${fixedAppointmentId}/change-log`,
      ),
    enabled: (enabled ?? true) && fixedAppointmentId > 0,
  });
};

/**
 * GET /fixed-appointment/:fixedAppointmentId/members/:customerId/booking-preview
 */
export const useGetFixedAppointmentMemberBookingPreview = ({
  fixedAppointmentId,
  customerId,
  enabled,
}: {
  fixedAppointmentId: number;
  customerId: number | null;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: [
      "fixed-appointment-member-booking-preview",
      fixedAppointmentId,
      customerId,
    ],
    queryFn: () =>
      get<GetFixedAppointmentMemberBookingPreview>(
        `/fixed-appointment/${fixedAppointmentId}/members/${customerId ?? 0}/booking-preview`,
      ),
    enabled:
      (enabled ?? true) && fixedAppointmentId > 0 && (customerId ?? 0) > 0,
  });
};

/**
 * POST /fixed-appointment/:fixedAppointmentId/members
 */
export const usePostFixedAppointmentMember = () => {
  return useCustomMutation({
    mutationFn: ({
      fixedAppointmentId,
      body,
    }: {
      fixedAppointmentId: number;
      body: PostFixedAppointmentMember["body"];
    }) =>
      post<PostFixedAppointmentMember>(
        `/fixed-appointment/${fixedAppointmentId}/members`,
        body,
      ),
    onSuccess: () => {
      invalidateFixedAppointmentQueries();
      queryClient.invalidateQueries({
        queryKey: ["fixed-appointment-member-booking-preview"],
      });
    },
  });
};

/**
 * PUT /fixed-appointment/:fixedAppointmentId/members/:customerId
 */
export const usePutFixedAppointmentMember = () => {
  return useCustomMutation({
    mutationFn: ({
      fixedAppointmentId,
      customerId,
      body,
    }: {
      fixedAppointmentId: number;
      customerId: number;
      body: PutFixedAppointmentMember["body"];
    }) =>
      put<PutFixedAppointmentMember>(
        `/fixed-appointment/${fixedAppointmentId}/members/${customerId}`,
        body,
      ),
    onSuccess: () => {
      invalidateFixedAppointmentQueries();
      queryClient.invalidateQueries({
        queryKey: ["fixed-appointment-change-log"],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
    },
  });
};

/**
 * DELETE /fixed-appointment/:fixedAppointmentId/members/:customerId
 */
export const useDeleteFixedAppointmentMember = () => {
  return useCustomMutation({
    mutationFn: ({
      fixedAppointmentId,
      customerId,
      body,
    }: {
      fixedAppointmentId: number;
      customerId: number;
      body?: DeleteFixedAppointmentMember["body"];
    }) =>
      del<DeleteFixedAppointmentMember>(
        `/fixed-appointment/${fixedAppointmentId}/members/${customerId}`,
        body,
      ),
    onSuccess: () => {
      invalidateFixedAppointmentQueries();
      queryClient.invalidateQueries({
        queryKey: ["fixed-appointment-member-booking-preview"],
      });
    },
  });
};

/**
 * PUT /fixed-appointment/:fixedAppointmentId/end-date
 */
export const usePutFixedAppointmentEndDate = () => {
  return useCustomMutation({
    mutationFn: ({
      fixedAppointmentId,
      body,
    }: {
      fixedAppointmentId: number;
      body: PutFixedAppointmentEndDate["body"];
    }) =>
      put<PutFixedAppointmentEndDate>(
        `/fixed-appointment/${fixedAppointmentId}/end-date`,
        body,
      ),
    onSuccess: () => {
      invalidateFixedAppointmentQueries();
      queryClient.invalidateQueries({
        queryKey: ["fixed-appointment-change-log"],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
    },
  });
};

/**
 * DELETE /fixed-appointment/:fixedAppointmentId
 */
export const useDeleteFixedAppointment = () => {
  return useCustomMutation({
    mutationFn: ({
      fixedAppointmentId,
      body,
    }: {
      fixedAppointmentId: number;
      body?: DeleteFixedAppointment["body"];
    }) =>
      del<DeleteFixedAppointment>(
        `/fixed-appointment/${fixedAppointmentId}`,
        body,
      ),
    onSuccess: () => {
      invalidateFixedAppointmentQueries();
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
    },
  });
};
